import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Globe, Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/request-reset`, { email });
      setEmailSent(true);
      toast.success('If an account exists, a reset code has been sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request reset');
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToReset = () => {
    navigate(`/reset-password?email=${encodeURIComponent(email)}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-coral-50 via-peach-50 to-teal-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Globe className="w-10 h-10 text-coral" />
            <h1 className="text-3xl font-bold">CultureMeet</h1>
          </div>
          <p className="text-gray-600">Reset your password</p>
        </div>

        <div className="glass-dark rounded-3xl p-8 shadow-2xl">
          <button
            onClick={() => navigate('/auth')}
            className="flex items-center space-x-2 text-gray-600 hover:text-coral transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to login</span>
          </button>

          {!emailSent ? (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold mb-2">Forgot Password?</h2>
                <p className="text-gray-600 text-sm mb-4">
                  Enter your email address and we'll send you a reset code.
                </p>
              </div>

              <div>
                <Label htmlFor="email" className="text-gray-700 font-medium">
                  Email Address
                </Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="email"
                    data-testid="forgot-email-input"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 py-6 rounded-xl border-gray-200"
                    required
                  />
                </div>
              </div>

              <Button
                data-testid="request-reset-btn"
                type="submit"
                disabled={loading}
                className="w-full py-6 rounded-xl text-lg font-semibold bg-black hover:bg-gray-800 text-white"
              >
                {loading ? 'Sending...' : 'Send Reset Code'}
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border-2 border-green-500 rounded-xl p-6 mb-4 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-green-800 font-bold text-lg mb-2">Check Your Email!</p>
                <p className="text-green-700 text-sm">
                  We've sent a 6-digit reset code to <strong>{email}</strong>
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Didn't receive it?</strong> Check your spam folder or wait a few minutes. The code expires in 1 hour.
                </p>
              </div>

              <Button
                data-testid="proceed-reset-btn"
                onClick={handleProceedToReset}
                className="w-full py-6 rounded-xl text-lg font-semibold bg-gradient-to-r from-teal to-teal-light text-white"
              >
                I Have My Code → Continue
              </Button>

              <button
                onClick={() => {
                  setEmailSent(false);
                }}
                className="w-full text-center text-gray-600 hover:text-coral transition-colors text-sm"
              >
                Try a different email
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
